import { JSX, useEffect, useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";

import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { ButtonOwnProps, Chip, Dialog, DialogActions, Paper, SxProps, Typography } from "@mui/material";
import { Box, Button, Container, DialogContentText, Grid, Stack } from "@mui/material";

import conf from "../../conf";
import Spinner from "./Spinner";
import SectionHeader from "./SectionHeader";
import TermsAndConditions from "./TermsAndConditions";
import { Idle, Offline, Online } from "./Status";
import { useConfgOrganizationDlmVerify } from "../../hooks/confg/base";

type ConfirmationViewProps = {
  open: boolean;
  status?: UseMutationResult["status"];
  message: string | JSX.Element;
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
  onCancelLabel?: string;
  onCancelLoading?: boolean;
};
const ConfirmationView = (props: ConfirmationViewProps) => {
  const { open, status, message, onClose, onConfirm, onCancel, onCancelLabel } = props;

  const [agreed, setAgreed] = useState(false);

  const verify = useConfgOrganizationDlmVerify();

  const handleVerify = () => verify.mutate();
  const handleAgreed = (e) => setAgreed(e.target.checked);
  const handleConfirm = () => onConfirm();

  useEffect(() => {
    if (open) handleVerify();
    else setAgreed(false);
  }, [open]);

  useEffect(() => {
    if (status === "success") {
      setAgreed(false);
      onClose();
    }
  }, [status]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="push-confirmation-button"
          icon={<WarningIcon />}
          title="Confirm: PUSH"
          buttons={[
            <Button key={"confirmation-button-close"} color="inherit" onClick={onClose}>
              <CloseIcon />
            </Button>
          ]}
        />

        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container p={1} border={1} borderRadius={1}>
                <Grid item xs={12} sm={12}>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography gutterBottom variant="h6" component="div">
                      Status
                    </Typography>
                    {verify.isSuccess ? (
                      <Chip size="small" label={"online"} icon={Online} />
                    ) : verify.isError ? (
                      <Chip size="small" label={"offline"} icon={Offline} />
                    ) : (
                      <Chip size="small" label={"unknown"} icon={Idle} />
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <DialogContentText variant="caption">
                    The following is the current status of the configured DLM push target. If the target is offline,
                    continuing with this action will add the asset package into a queue & move the imported key into a
                    "scheduled" state.
                  </DialogContentText>
                </Grid>

                <Grid item sm={12}>
                  <Paper
                    variant="outlined"
                    elevation={3}
                    sx={{
                      p: 1,
                      opacity: 1,
                      textAlign: "left"
                    }}
                  >
                    {verify.status === "idle" && (
                      <Typography variant="caption">Click 'Verify' to view current status.</Typography>
                    )}

                    {verify.status == "success" && (
                      <Typography variant="caption" color="success">
                        <i>{JSON.stringify(verify.data.data)}</i>
                      </Typography>
                    )}

                    {verify.status == "error" && (
                      <Typography variant="caption" color={"error"}>
                        <i>{verify.error.response?.data.error}</i>
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item sm={12}>
                  <DialogActions>
                    <Button size="small" color="inherit" onClick={() => verify.reset()}>
                      {verify.isPending ? "Cancel" : "Reset"}
                    </Button>

                    <Button size="small" variant="contained" onClick={handleVerify}>
                      {verify.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Verify"}
                    </Button>
                  </DialogActions>
                </Grid>
              </Grid>

              <Grid container p={1} mt={1} border={1} borderRadius={1}>
                <DialogContentText>{message}</DialogContentText>
              </Grid>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, mb: 2 }}>
            <TermsAndConditions type="custom" agreed={agreed} handleAgreed={handleAgreed} />

            <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
              <Button variant="outlined" onClick={onCancel ?? onClose}>
                {props.onCancelLoading ? <Spinner sx={{ mx: 2 }} /> : (onCancelLabel ?? "Cancel")}
              </Button>

              <Button color="error" variant="contained" disabled={!agreed} onClick={handleConfirm}>
                Confirm
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

type PushWithConfirmProps = {
  sx?: SxProps;
  size?: ButtonOwnProps["size"];
  color?: ButtonOwnProps["color"];
  status?: UseMutationResult["status"];
  message: string | JSX.Element;
  variant?: ButtonOwnProps["variant"];
  disabled?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onCancelLabel?: string;
  onCancelLoading?: boolean;
  fullWidth?: boolean;
  showButton?: boolean;
  defaultOpen?: boolean;
};

const PushWithConfirm = (props: PushWithConfirmProps) => {
  const {
    size = "medium",
    color = "secondary",
    variant = "contained",
    disabled = false,
    status,
    message,
    isLoading,
    onConfirm,
    onCancel,
    onCancelLabel,
    onCancelLoading,
    fullWidth = false,
    showButton = true,
    defaultOpen = false,
    sx
  } = props;

  const [open, setOpen] = useState(defaultOpen);

  const onClick = () => setOpen(true);
  const onClose = () => setOpen(false);

  return (
    <div>
      {showButton && (
        <Button
          size={size}
          color={color}
          variant={variant}
          disabled={disabled}
          fullWidth={fullWidth}
          onClick={onClick}
          sx={sx}
        >
          {isLoading ? <Spinner sx={{ mx: 2 }} /> : "Push"}
        </Button>
      )}

      <ConfirmationView
        open={open}
        status={status}
        message={message}
        onClose={onClose}
        onCancel={onCancel}
        onConfirm={onConfirm}
        onCancelLabel={onCancelLabel}
        onCancelLoading={onCancelLoading}
      />
    </div>
  );
};

export default PushWithConfirm;
